import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PageTitle from "../../components/PageTitle";
import {
  toggleMenu,
  displayFormatDate,
  formatToDateOnly,
} from "../../utils/helpers";
import NoRecordsAvailable from "../../components/NoRecordsAvailable";
import axios from "axios";
import Cookies from "js-cookie";
import { useEmployeeNamesListStore } from "../../stores/employeeNamesListStore";
import { TailSpin, ThreeDots } from "react-loader-spinner";
import { API_HR_PENDING_EXTENSION_TIMESHEETS } from "../../config/Endpoints";
import WFHTimesheetHRRequestsModal from "./WFHTimesheetHRRequestsModal";
import {
  Badge,
  Button,
  DateRange,
  ResponsiveDataTable,
  Select,
} from "../../v2/components/ui/index.ts";
import { FiEye } from "react-icons/fi";
import { Filter as FilterIcon, RotateCcw } from "lucide-react";

const TYPE_OPTIONS = [

  { value: "WFH", label: "Work from Home" },
  { value: "REGULAR", label: "Regular" },
  { value: "REMOTE", label: "Remote" },
  { value: "CONSULTANT", label: "Consultant" },
];

function formatTypeLabel(type) {
  if (type == null || type === "") return "-";
  const normalized = String(type).trim().toUpperCase();
  if (normalized === "WFH") return "Work from Home";
  if (normalized === "REGULAR") return "Regular";
  if (normalized === "REMOTE") return "Remote";
  if (normalized === "CONSULTANT") return "Consultant";
  return type;
}

function formatStatusLabel(status) {
  if (status == null || status === "") return "-";
  const normalized = String(status).trim().toUpperCase();
  if (normalized === "APPROVED") return "Approved";
  if (normalized === "REJECTED") return "Rejected";
  if (normalized === "PENDING") return "Pending";
  return status
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function statusBadgeVariant(status) {
  if (status == null || status === "") return "neutral";
  const normalized = String(status).trim().toUpperCase();
  if (normalized === "APPROVED") return "success";
  if (normalized === "REJECTED") return "danger";
  if (normalized === "PENDING") return "warning";
  return "neutral";
}

function parsePagedResponse(payload) {
  if (Array.isArray(payload)) {
    return {
      items: payload,
      totalElements: payload.length,
      totalPages: 1,
    };
  }

  const items = payload?.content ?? payload?.data ?? [];
  const list = Array.isArray(items) ? items : [];

  return {
    items: list,
    totalElements:
      typeof payload?.totalElements === "number"
        ? payload.totalElements
        : list.length,
    totalPages:
      typeof payload?.totalPages === "number" ? payload.totalPages : 1,
  };
}

function mapRow(item) {
  return {
    employeeName: item.employeeName,
    type: item.type,
    status: item.status,
    requestedOn: item.requestedOn,
    requestedFor: item.requestedFor,
    totalExtensions: item.totalExtensions,
    _original: item,
  };
}

const WFHTimesheetHRRequests = ({ title, description }) => {
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedTimesheet, setSelectedTimesheet] = useState(null);
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [selectedType, setSelectedType] = useState("");
  const [selectedEmployeeId, setSelectedEmployeeId] = useState("");
  const [appliedFromDate, setAppliedFromDate] = useState(null);
  const [appliedToDate, setAppliedToDate] = useState(null);
  const [appliedType, setAppliedType] = useState("");
  const [appliedEmployeeId, setAppliedEmployeeId] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalElements, setTotalElements] = useState(0);
  const accessToken = Cookies.get("accessToken");
  const [isFirstLoad, setIsFirstLoad] = useState(true);
  const isFirstLoadRef = useRef(true);
  const {
    employeeList,
    fetchEmployees,
    loading: employeesLoading,
  } = useEmployeeNamesListStore();

  useEffect(() => {
    fetchEmployees();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const employeeSelectOptions = useMemo(
    () =>
      (employeeList || []).map((o) => ({
        value: String(o.value),
        label: o.label,
      })),
    [employeeList],
  );

  const fetchTimesheets = useCallback(async () => {
    if (!isFirstLoadRef.current) setLoading(true);

    try {
      const query = new URLSearchParams();
      query.append("page", String(Math.max(0, page - 1)));
      query.append("size", String(pageSize));

      if (appliedFromDate) {
        query.append("fromDate", formatToDateOnly(appliedFromDate));
      }
      if (appliedToDate) {
        query.append("toDate", formatToDateOnly(appliedToDate));
      }
      if (appliedType) {
        query.append("type", appliedType);
      }
      if (appliedEmployeeId) {
        query.append("employeeId", appliedEmployeeId);
      }

      const res = await axios.get(
        `${API_HR_PENDING_EXTENSION_TIMESHEETS}?${query.toString()}`,
        {
          headers: { Authorization: `Bearer ${accessToken}` },
        },
      );

      const { items, totalElements: total } = parsePagedResponse(res.data);
      setTableData(items.map(mapRow));
      setTotalElements(total);
    } catch {
      setTableData([]);
      setTotalElements(0);
    } finally {
      setLoading(false);
      if (isFirstLoadRef.current) {
        isFirstLoadRef.current = false;
        setIsFirstLoad(false);
      }
    }
  }, [
    accessToken,
    appliedFromDate,
    appliedToDate,
    appliedType,
    appliedEmployeeId,
    page,
    pageSize,
  ]);

  useEffect(() => {
    void fetchTimesheets();
  }, [fetchTimesheets]);

  const handleFilter = () => {
    setAppliedFromDate(fromDate);
    setAppliedToDate(toDate);
    setAppliedType(selectedType);
    setAppliedEmployeeId(selectedEmployeeId);
    setPage(1);
  };

  const handleClearFilter = () => {
    setFromDate(null);
    setToDate(null);
    setSelectedType("");
    setSelectedEmployeeId("");
    setAppliedFromDate(null);
    setAppliedToDate(null);
    setAppliedType("");
    setAppliedEmployeeId("");
    setPage(1);
  };

  const handleDateRangeChange = ([start, end]) => {
    setFromDate(start);
    setToDate(end);
  };

  const columns = useMemo(
    () => [
      {
        id: "employeeName",
        header: "Employee Name",
        accessor: "employeeName",
        sortable: true,
      },
      {
        id: "type",
        header: "Type",
        sortable: true,
        getSortValue: (row) => row.type ?? "",
        render: (row) => formatTypeLabel(row.type),
      },

      {
        id: "requestedOn",
        header: "Requested On",
        sortable: true,
        getSortValue: (row) => row.requestedOn ?? "",
        render: (row) =>
          row.requestedOn ? displayFormatDate(row.requestedOn) : "-",
      },
      {
        id: "requestedFor",
        header: "Requested For",
        sortable: true,
        getSortValue: (row) => row.requestedFor ?? "",
        render: (row) =>
          row.requestedFor ? displayFormatDate(row.requestedFor) : "-",
      },
      {
        id: "totalExtensions",
        header: "Total Extensions",
        sortable: true,
        getSortValue: (row) => row.totalExtensions ?? 0,
        render: (row) =>
          row.totalExtensions != null ? row.totalExtensions : "-",
      },
      {
        id: "action",
        header: "Action",
        sortable: false,
        render: (row) => (
          <a
            href="#wfhTimesheetHRRequestsModal"
            data-bs-toggle="modal"
            data-bs-target="#wfhTimesheetHRRequestsModal"
            onClick={() => setSelectedTimesheet(row._original)}
            className="action-btn action-btn--view"
          >
            <FiEye size={14} />
            View
          </a>
        ),
      },
    ],
    [],
  );

  return (
    <div className="wrapper-body">
      {isFirstLoad && (employeesLoading || loading) && (
        <div className="overlay">
          <div className="mt-5 text-center">
            <TailSpin
              height="100"
              width="100"
              radius="1"
              color="blue"
              ariaLabel="tail-spin-loading"
            />
          </div>
        </div>
      )}

      <div className="page-heading">
        <div id="showMenuBtn" className="collapse-button" onClick={toggleMenu}>
          <span></span>
          <span></span>
          <span></span>
        </div>
        <PageTitle title={title} description={description} />
      </div>

      <div className="card">
        <div className="filters d-flex gap-2 mb-3 flex-wrap align-items-end">
          <div className="filter flex-grow-1">
            <Select
              label="Employee Name"
              options={employeeSelectOptions}
              value={selectedEmployeeId}
              onChange={setSelectedEmployeeId}
              placeholder="Select Employee Name"
              isClearable
              searchable
            />
          </div>

          <div className="filter flex-grow-1">
            <DateRange
              label="Date Range"
              startDate={fromDate}
              endDate={toDate}
              onChange={handleDateRangeChange}
              maxDate={new Date()}
              placeholderText="Select Date Range"
            />
          </div>

          <div className="filter flex-grow-1">
            <Select
              label="Type"
              options={TYPE_OPTIONS}
              value={selectedType}
              onChange={setSelectedType}
              placeholder="Select Type"
              isClearable
            />
          </div>

          <div className="d-flex gap-2 ms-auto flex-wrap">
            <Button
              type="button"
              variant="accent"
              onClick={handleFilter}
              disabled={loading}
              startIcon={<FilterIcon size={14} aria-hidden />}
            >
              Apply Filter
            </Button>
            <Button
              type="button"
              variant="light-grey"
              onClick={handleClearFilter}
              disabled={loading}
              startIcon={<RotateCcw size={14} aria-hidden />}
            >
              Clear Filter
            </Button>
          </div>
        </div>

        {isFirstLoad ? null : loading ? (
          <div className="d-flex justify-content-center">
            <ThreeDots height="50" width="80" color="var(--color-accent-500)" />
          </div>
        ) : tableData.length > 0 ? (
          <ResponsiveDataTable
            pagination
            serialNumber
            columns={columns}
            data={tableData}
            page={page}
            pageSize={pageSize}
            totalItems={totalElements}
            onPageChange={setPage}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setPage(1);
            }}
            pageSizeOptions={[5, 10, 20, 30, 40]}
            totalLabel={`Total Requests: ${totalElements}`}
            aria-label="WFH HR timesheet extension requests"
          />
        ) : (
          <NoRecordsAvailable />
        )}
      </div>

      <div
        className="modal fade"
        id="wfhTimesheetHRRequestsModal"
        tabIndex="-1"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-lg modal-dialog-centered">
          <div className="modal-content">
            {selectedTimesheet && (
              <WFHTimesheetHRRequestsModal
                timesheet={selectedTimesheet}
                onClose={() => setSelectedTimesheet(null)}
                refreshList={fetchTimesheets}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default WFHTimesheetHRRequests;
